/** @jsxImportSource @emotion/react */
import { css, Theme, useTheme } from '@emotion/react';
import React, { FC, useMemo } from 'react';
import { FaEyeSlash, FaStar } from 'react-icons/fa';
import { MdCreate, MdRoom } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Box, Flex } from 'components/styled';
import Text from 'components/styled/Text';
import Link from 'components/styled/Link';
import FloatingActionsButton from 'components/floatingactions/FAButton';
import SubButton from 'components/floatingactions/SubButton';
import Checkbox from 'components/form/checkbox/Checkbox';
import AspectRatio from 'components/styled/AspectRatio';

import { getDateString, pluralRules } from 'utils';
import { PersonIcon, RevealsIcon } from 'assets';

import { Backend } from 'api/endpoints';

import { useExportItem } from 'hooks/useExport';
import useCompanyLogo from 'hooks/useCompanyLogo';

import { isOfferActive } from 'utils/EASHelpers';
import { EmploymentTypeText } from 'utils/enumMaps';
import { useLocalizedMultistring } from 'utils/localization';

import ToggleOfferAction from './ToggleOfferAction';

type SearchProps = {
	variant: 'search';
	score?: number;
	isPublic?: boolean;
} & Pick<
	Backend.OfferDetail | Backend.OfferList,
	| 'id'
	| 'title'
	| 'company'
	| 'companyExternal'
	| 'address'
	| 'employmentTypes'
	| 'businessSectors'
	| 'validTo'
	| 'validFrom'
	| 'recommended'
>;

type EditableProps = {
	variant: 'editable' | 'admin' | 'profile';
	disableTitle?: string;
} & Pick<
	Backend.Offer,
	| 'id'
	| 'title'
	| 'company'
	| 'companyExternal'
	| 'address'
	| 'employmentTypes'
	| 'businessSectors'
	| 'isVisible'
	| 'submittedForms'
	| 'validTo'
	| 'validFrom'
	| 'recommended'
	| 'viewsCount'
>;

type Props = { isCompany?: boolean } & (SearchProps | EditableProps);

const OfferSearchItem: FC<Props> = ({
	id,
	title,
	company,
	companyExternal,
	address,
	employmentTypes,
	isCompany,
	...p
}) => {
	// History
	const { push } = useHistory();

	const localize = useLocalizedMultistring();
	const { t, i18n } = useTranslation('components/tiles/offer_search_item/');

	//TODO: companyExternal public logo
	const companyLogo = useCompanyLogo(
		id,
		company?.logo,
		p.variant === 'search' && p.isPublic,
	);

	const { university } = useTheme();

	const isActive = useMemo(() => p.variant === 'search' || isOfferActive(p), [
		p,
	]);

	// Export
	const { exporting, selected, toggleSelected } = useExportItem(id);

	// Check for municipality or region existence to use it for pin display
	const isMunicipalityOrRegionGiven = useMemo(() => {
		if (
			address?.country?.code === 'CZ' &&
			(address?.region || address?.municipality)
		) {
			return true;
		} else if (
			address?.country?.code !== 'CZ' &&
			(address?.regionForeign || address?.municipalityForeign)
		) {
			return true;
		} else {
			return false;
		}
	}, [
		address?.country?.code,
		address?.municipality,
		address?.municipalityForeign,
		address?.region,
		address?.regionForeign,
	]);

	return (
		<AspectRatio ratio={[4, 2]} width="100%">
			<Flex
				height="100%"
				bg="white"
				width={1}
				p={3}
				flexDirection="column"
				textAlign="left"
				boxShadow={`8px 6px 30px rgba(0, 0, 0, ${selected ? '0.5' : '0.1'})`}
				position="relative"
			>
				{!isActive && (
					<Box
						as={FaEyeSlash}
						size={university === 'MUNI' ? 32 : 42}
						top={4}
						position="absolute"
						right={4}
						opacity={0.25}
					/>
				)}
				{university === 'MUNI' && p.recommended && (
					<Box
						as={FaStar}
						size={32}
						position="absolute"
						top={!isActive ? 72 : 4}
						right={4}
						color="primary"
					/>
				)}
				<Flex alignItems="center">
					{/* Export */}
					{exporting && (
						<Checkbox checked={selected} onSetValue={toggleSelected} />
					)}
					{/* Company logo */}
					<AspectRatio
						ratio={[1, 1]}
						width="60px"
						mr={3}
						flexShrink={0}
						css={(theme: Theme) => css`
							background-image: url(${companyLogo});
							background-size: cover;
							background-repeat: no-repeat;
							background-position: center;
							border: 1px solid ${theme.colors.darkerGrey};

							${!isActive &&
							css`
								filter: grayscale();
							`}
						`}
					/>
					<Flex flexDirection="column" mr={5}>
						<Link to={isCompany ? `/company` : `/company/${company?.id}`}>
							<Text>{localize(companyExternal?.name ?? company?.name)}</Text>
						</Link>
						<Text
							color={university === 'ZLIN' ? 'primary' : 'textLight'}
							mt={0}
						>
							{isMunicipalityOrRegionGiven && <Box as={MdRoom} mr={2} />}
							{address?.country?.code === 'CZ'
								? `${localize(address?.municipality?.multiName)}${
										address?.region && address?.municipality ? ', ' : ''
								  }${localize(address?.region?.multiName)}`
								: `${address?.municipalityForeign}${
										address?.regionForeign && address?.municipalityForeign
											? ', '
											: ''
								  }${address?.regionForeign}`}
						</Text>
					</Flex>
				</Flex>

				{/* Title */}
				<Link to={`/offer/${id}`} mt={2}>
					<Text fontSize="lg" fontWeight="bolder">
						{localize(title) || t('unnamed_position')}
					</Text>
				</Link>

				{/* Score */}
				{p.variant === 'search' && p.score ? (
					<Text my={0}>
						{t('score')}:{' '}
						<Text as="span" fontWeight="bold">
							{Math.round(p.score)}%
						</Text>
					</Text>
				) : null}

				{/* Employment type */}
				<Text>
					{employmentTypes?.map((e, idx) => (
						<Text
							as="span"
							key={e}
							color={e === 'BP' || e === 'DP' ? 'primary' : 'text'}
						>
							{localize(EmploymentTypeText[e])}
							{idx !== employmentTypes.length - 1 && (
								<Text as="span" color="textLight">
									,{' '}
								</Text>
							)}
						</Text>
					))}
				</Text>
				<Flex height="100%" alignItems="flex-end">
					{isActive ? (
						<Text
							color={university === 'ZLIN' ? 'primary' : 'textLight'}
							my={0}
						>
							{t('active_from_to', {
								from: getDateString(p.validFrom, i18n.language),
								to: getDateString(p.validTo, i18n.language),
							})}
						</Text>
					) : (
						<Text color="textLight" my={0}>
							{!p.validTo ? t('unpublished') : t('inactive')}
						</Text>
					)}
				</Flex>

				<Flex>
					{p.variant === 'editable' && (
						<Link
							py={2}
							pr={2}
							to={`/offer/candidates/${id}`}
							color="primary"
							alignSelf="flex-start"
						>
							<PersonIcon height={10} mr={1} />
							{pluralRules(p.submittedForms.length, {
								one: t('one'),
								few: t('few'),
								many: t('many'),
							})}
						</Link>
					)}

					{p.variant === 'admin' && (
						<Text>
							<PersonIcon height={10} mr={1} mb={1} />
							{pluralRules(p.submittedForms.length, {
								one: t('one'),
								few: t('few'),
								many: t('many'),
							})}
						</Text>
					)}
					{p.variant !== 'search' && (
						<Text>
							<RevealsIcon height={12} ml={2} mb={1} />
							{pluralRules(p.viewsCount, {
								one: t('one_view'),
								few: t('many_views'),
								many: t('many_views'),
							})}
						</Text>
					)}
				</Flex>

				{/* Actions */}
				{p.variant !== 'search' && (
					<Box position="absolute" bottom={4} right={4}>
						<FloatingActionsButton id={`${id}-card-actions`}>
							<SubButton
								icon={<MdCreate size={15} color="white" />}
								size={35}
								tooltip={t('edit')}
								onClick={() => push(`/offer/edit/${id}`)}
								backgroundColor="primary"
							/>
							<ToggleOfferAction
								id={id}
								isActive={isActive}
								employmentTypes={employmentTypes}
							/>
						</FloatingActionsButton>
					</Box>
				)}
			</Flex>
		</AspectRatio>
	);
};
export default OfferSearchItem;
